import axiosInstance from "./http";
// import { dummyActiveCarePlans } from "./dummyData";

export const fetchSections = async () => {
  try {
    const response = await axiosInstance.get("sections/");
    return response.data;
  } catch (err) {
    throw err;
  }
};

export const fetchSectionItems = async (sectionId) => {
  try {
    const response = await axiosInstance.get(`sections/${sectionId}/`);
    return response.data;
  } catch (err) {
    throw err;
  }
};

// Service to update the item order in a section
export const updateSectionItemsOrder = async (sectionId, sectionData) => {
  try {
    const response = await axiosInstance.patch(
      `/sections/${sectionId}/`,
      sectionData
    );
    return response.data;
  } catch (err) {
    throw err;
  }
};

// Careplan Builder -> Items Related Services
export const fetchItems = async () => {
  try {
    const response = await axiosInstance.get("items/");
    return response.data;
  } catch (err) {
    throw err;
  }
};

export const createItem = async (itemData) => {
  try {
    const response = await axiosInstance.post("items/", itemData);
    return response;
  } catch (err) {
    throw err;
  }
};

export const updateItem = async (id, itemData) => {
  try {
    const response = await axiosInstance.put(`items/${id}/`, itemData);
    return response;
  } catch (err) {
    throw err;
  }
};

export const deleteItem = async (itemId) => {
  try {
    const response = await axiosInstance.delete(`items/${itemId}/`);
    return response;
  } catch (err) {
    throw err;
  }
};

export const getCarePlanInstance = async (carePlanId) => {
  try {
    const response = await axiosInstance.get(`/careplans/${carePlanId}/`);
    return response.data; // Should return the care plan instance data with sections and answers
  } catch (error) {
    throw error;
  }
};

export const getDataForViewService = async (publicId) => {
  try {
    const response = await axiosInstance.get(`/careplans/${publicId}/view/`);
    return response.data; // Should return the care plan instance data with sections and answers
  } catch (error) {
    throw error;
  }
};

export const fetchActiveCarePlansService = async (
  page,
  rowsPerPage,
  searchQuery,
  ordering = ""
) => {
  const { nhsNumber, createdBy } = searchQuery || {};
  const params = { page, page_size: rowsPerPage };

  if (nhsNumber) params.nhsNumber = nhsNumber;
  if (createdBy) params.created_by = createdBy;
  if (ordering) params.ordering = ordering;

  try {
    const response = await axiosInstance.get("/careplans/active/", { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const activeCarePlanDetailViewService = async (carePlanId) => {
  const response = await axiosInstance.get(`/careplans/active/${carePlanId}/`);
  return response.data;
};

export const fetchDraftCarePlansService = async (
  page,
  rowsPerPage,
  searchQuery,
  ordering = ""
) => {
  const { nhsNumber, createdBy } = searchQuery || {}; // for search draft care plan
  const params = { page, page_size: rowsPerPage };

  if (nhsNumber) params.nhsNumber = nhsNumber;
  if (createdBy) params.created_by = createdBy;
  if (ordering) params.ordering = ordering;

  try {
    const response = await axiosInstance.get("/careplans/drafts/", { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const fetchSectionAuditLogs = async (sectionId) => {
  try {
    const response = await axiosInstance.get(
      `/sections/${sectionId}/audit_logs/`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const fetchItemAuditLogs = async (itemId) => {
  try {
    const response = await axiosInstance.get(`/items/${itemId}/audit_logs/`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const fetchCarePlanAuditLogsService = (
  id,
  isDraft = false,
  page = 1,
  pageSize = 12
) =>
  axiosInstance
    .get(`/careplans/${id}/${isDraft ? "draft/audit_logs" : "audit_logs"}/`, {
      params: { page, page_size: pageSize },
    })
    .then((r) => r.data);

export const fetchSystemAuditLogsService = (
  page = 1,
  pageSize = 12,
  search = "",
  date_from,
  date_to
) =>
  axiosInstance
    .get("/system/audit_logs/", {
      params: {
        page,
        page_size: pageSize,
        search,
        ...(date_from ? { date_from } : {}),
        ...(date_to ? { date_to } : {}),
      },
    })
    .then((r) => r.data);

export const fetchAllCarePlanAuditLogs = (
  page = 1,
  pageSize = 12,
  search = "",
  date_from,
  date_to
) =>
  axiosInstance
    .get("/careplans/audit_logs/", {
      params: {
        page,
        page_size: pageSize,
        search,
        ...(date_from ? { date_from } : {}),
        ...(date_to ? { date_to } : {}),
      },
    })
    .then((r) => r.data);

export const fetchBuilderAuditLogsService = (
  page = 1,
  pageSize = 12,
  search = "",
  date_from,
  date_to
) =>
  axiosInstance
    .get("/builder/audit_logs/", {
      params: {
        page,
        page_size: pageSize,
        search,
        ...(date_from ? { date_from } : {}),
        ...(date_to ? { date_to } : {}),
      },
    })
    .then((r) => r.data);

export const fetchUnifiedAuditLogsService = (
  page = 1,
  pageSize = 12,
  search = "",
  date_from,
  date_to
) =>
  axiosInstance
    .get("/unified/audit_logs/", {
      params: {
        page,
        page_size: pageSize,
        search,
        ...(date_from ? { date_from } : {}),
        ...(date_to ? { date_to } : {}),
      },
    })
    .then((r) => r.data);

export const logSystemEventService = (actionText) =>
  axiosInstance
    .post("/system/audit_logs/", { action: actionText })
    .then((res) => res);

export const checkCarePlanExistsService = async (nhsNumber) => {
  try {
    const response = await axiosInstance.post("/careplans/check_careplan/", {
      nhs_number: nhsNumber,
    });
    return response.data; // { exists: true/false }
  } catch (error) {
    throw error;
  }
};

export const createCarePlanService = async (serializedCarePlanData) => {
  try {
    const response = await axiosInstance.post(
      "/careplans/",
      serializedCarePlanData
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const checkOrCreateCarePlanService = async (nhsNumber) => {
  try {
    const response = await axiosInstance.post("/careplans/check_or_create/", {
      nhs_number: nhsNumber,
    });
    return response.data; // Should return the care plan data
  } catch (error) {
    throw error;
  }
};

export const saveCarePlanService = async (carePlanData, isEdit) => {
  const url = isEdit ? `/careplans/${carePlanData.id}/` : `/careplans/`;
  const method = isEdit ? "put" : "post";
  try {
    const response = await axiosInstance[method](url, carePlanData);
    return response;
  } catch (error) {
    throw error;
  }
};

// Add MDT users to care plan
export const addMdtUserToCarePlanService = async (
  carePlanId,
  selectedMdtUsers
) => {
  const response = await axiosInstance.post(
    `/careplans/${carePlanId}/add-mdt-users`,
    {
      users: selectedMdtUsers.map((user) => user.id),
    }
  );
  return response.data;
};

// Delete care plan
export const deleteCarePlanService = async (carePlanId) => {
  try {
    const response = await axiosInstance.delete(`/careplans/${carePlanId}/`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const addUsersToCarePlanService = async (
  carePlanId,
  userAssignments
) => {
  // userAssignments is an array of objects like [{ user_id: 1, can_edit: true }, { user_id: 2, can_edit: false }]
  try {
    const response = await axiosInstance.post("/careplans/assign_users/", {
      care_plan_id: carePlanId,
      users: userAssignments,
    });
    return response.data; // This should return updated care plan or assigned users
  } catch (error) {
    throw error;
  }
};

export const removeUserFromCarePlanService = async (carePlanId, userId) => {
  // userAssignments is an array of objects like [{ user_id: 1, can_edit: true }, { user_id: 2, can_edit: false }]
  try {
    const response = await axiosInstance.post("/careplans/remove_user/", {
      care_plan_id: carePlanId,
      user_id: userId,
    });
    return response.data; // This should return updated care plan or assigned users
  } catch (error) {
    throw error;
  }
};

export const createDraftCarePlanService = async (carePlanId) => {
  // userAssignments is an array of objects like [{ user_id: 1, can_edit: true }, { user_id: 2, can_edit: false }]
  try {
    const response = await axiosInstance.post(
      `/careplans/${carePlanId}/draft/`
    );
    return response.data; // This should return updated care plan or assigned users
  } catch (error) {
    throw error;
  }
};

export const publishCarePlanService = async (carePlanId, serviceUserName) => {
  // userAssignments is an array of objects like [{ user_id: 1, can_edit: true }, { user_id: 2, can_edit: false }]
  try {
    const response = await axiosInstance.post(
      `/careplans/${carePlanId}/publish/`,
      { service_user_name: serviceUserName } // Include the name in the request body
    );
    return response.data; // This should return updated care plan or assigned users
  } catch (error) {
    throw error;
  }
};

export const lockCarePlanService = async (carePlanId) => {
  try {
    const response = await axiosInstance.post(`/careplans/${carePlanId}/lock/`);
    return {
      success: response.data.success,
      locked_by: response.data.locked_by,
      is_current_user: true, // Added flag
    };
  } catch (error) {
    if (error.response?.status === 409) {
      const currentUser = JSON.parse(localStorage.getItem("userProfile")) || {};
      // Create a proper Error object
      const customError = new Error(error.response.data.message);
      // Attach additional properties
      customError.code = error.response.data.code;
      customError.locked_by = error.response.data.locked_by;
      customError.is_current_user =
        error.response.data.locked_by.id === currentUser.id;
      throw customError;
      // throw {
      //   code: error.response.data.code,
      //   locked_by: error.response.data.locked_by,
      //   message: error.response.data.message,
      //   is_current_user: error.response.data.locked_by.id === currentUser.id, // Compare with current user
      // };
    }
    throw error;
  }
};
export const unlockCarePlanService = async (carePlanId) => {
  try {
    const response = await axiosInstance.post(
      `/careplans/${carePlanId}/unlock/`
    );
    return response.data; // Expecting { success: "Care Plan unlocked successfully" }
  } catch (error) {
    throw error; // Let the thunk handle the rejection
  }
};

export const checkLockStatus = async (carePlanId) => {
  try {
    const response = await axiosInstance.get(
      `/careplans/${carePlanId}/lock_status/`
    );
    return response.data;
  } catch (error) {
    console.error("Error checking lock status:", error);
    throw error;
  }
};

export const overviewService = async () => {
  // userAssignments is an array of objects like [{ user_id: 1, can_edit: true }, { user_id: 2, can_edit: false }]
  try {
    const response = await axiosInstance.get("/overview-stats/");
    return response.data; // This should return updated care plan or assigned users
  } catch (error) {
    throw error;
  }
};

// Update the resource link and description
export const updateSectionService = async (sectionId, data) => {
  // data = { resource_link, qr_description }
  const response = await axiosInstance.patch(`/sections/${sectionId}/`, data);
  return response.data;
};

// Service calls for DefaultContact model:

export const fetchDefaultContactsService = async () => {
  const response = await axiosInstance.get("/careplans/default-contacts/");
  return response.data; // an array of default contacts
};

export const createDefaultContactService = async (data) => {
  const response = await axiosInstance.post(
    "/careplans/default-contacts/",
    data
  );
  return response.data; // the newly created contact
};

export const updateDefaultContactService = async (contactId, data) => {
  const response = await axiosInstance.put(
    `/careplans/default-contacts/${contactId}/`,
    data
  );
  return response.data; // updated contact
};

export const deleteDefaultContactService = async (contactId) => {
  // If your backend uses a different route, adjust accordingly
  return axiosInstance.delete(`/careplans/default-contacts/${contactId}/`);
};

export const sendFeedbackService = (publicId, { name, email, feedback }) =>
  axiosInstance.post(`/careplans/${publicId}/feedback/`, {
    name,
    email,
    comment: feedback,
  });

export const fetchFeedbacksService = (publicId) =>
  axiosInstance
    .get(`/careplans/${publicId}/feedback/list/`)
    .then((res) => res.data);

export const logDraftCarePlanActionService = (draftCarePlanId, type) =>
  axiosInstance
    .post(`/careplan/draft/${draftCarePlanId}/log_draft_action/?type=${type}`)
    .then((res) => res.data);

export const getGuideTitles = () =>
  axiosInstance.get("/guides/").then((res) => res.data);

export const getGuideDetail = (id) =>
  axiosInstance.get(`guides/${id}/`).then((res) => res.data);

export const logPrintLanguage = (publicId, language) =>
  axiosInstance.post(`public/careplan/${publicId}/log-print-language/`, {
    language,
  });
